/**
 * AutoChargeUsers Component
 * --
 * 작성자 - Owen
 * 작성일 - 2025 07 18 금요일
 * 마지막 수정일 - 2025 07 18 금요일
 */
import React from 'react';
import { Button, Table, Tag } from 'antd';
import { Content, Title, ModalLayout } from 'components';
import { strCut, stringToMoneyFormat } from 'utils';
import dayjs from 'dayjs';
import { UserDetail } from 'routes/admin.routes/admin.users';
import { ORDER_ROUTE } from 'constants/OrderConstants';
import { JM_PAY_TYPE } from 'constants/JMPayConstant';

const AutoChargeUsers = (props) => {
  /**
   * SECTION - Init
   */
  const TYPE = React.useMemo(() => {
    return {
      MIN_PRICE_CHARGE: {
        title: '최소금액 이하 자동 충전',
        color: '',
        subKey: 'jm_min_price',
      },
      MONTH_CHARGE: {
        title: '월 단위 자동 충전',
        color: '',
        subKey: 'jm_day',
      },
    };
  }, []);

  /**!SECTION - Init */

  /**
   * SECTION - Route
   */

  /**!SECTION - Route */

  /**
   * SECTION - Props
   */
  const { users = [], history = [], currentRow, setCurrentRow } = props;
  /**!SECTION - Props */

  /**
   * SECTION - Route
   */

  /**!SECTION - Route */

  /**
   * SECTION - Variable
   */

  /**!SECTION - Variable */

  /**
   * SECTION - Query Data
   */

  /**!SECTION - Query Data */

  /**
   * SECTION - Mutate
   */

  /**!SECTION - Mutate */

  /**
   * SECTION - State
   */

  /**!SECTION - State */

  /**
   * SECTION - Function
   */

  /**!SECTION - Function */

  /**
   * SECTION - Hooks
   */
  const columns = React.useMemo(() => {
    return [
      {
        title: '유저등급',
        dataIndex: 'grade',
        width: 145,
        align: 'center',
        render: (value) => value?.grade_name,
      },
      {
        title: '회원명',
        dataIndex: 'user_name',
        width: 200,
        align: 'center',
        render: (value, record) => (
          <a
            style={{ textDecoration: 'underline' }}
          >{`${value}(${record?.user_gender}, ${record?.user_nickname})`}</a>
        ),
      },
      {
        title: '생년월일',
        dataIndex: 'user_birthday',
        width: 130,
        align: 'center',
        render: (value) => dayjs(value * 1000).format('YYYY-MM-DD'),
      },
      {
        title: '연락처',
        dataIndex: 'user_phone',
        width: 150,
        align: 'center',
      },
      {
        title: '유저상태',
        dataIndex: 'account_status',
        width: 120,
        align: 'center',
      },

      {
        title: 'JM페이 잔액',
        dataIndex: 'jm_pay',
        width: 150,
        align: 'center',
        render: (value) => stringToMoneyFormat(value),
        sorter: (a, b) => a.jm_pay - b.jm_pay,
      },
      {
        title: '충전유형',
        key: 'type',
        dataIndex: 'jm_pay_setting',
        width: 200,
        align: 'center',
        render: (value) => (
          <Tag color={TYPE[value?.charging_type]?.color}>
            {TYPE[value?.charging_type]?.title}
          </Tag>
        ),
        filters: Object.entries(TYPE).map(([key, v]) => ({
          text: v.title,
          value: key,
        })),
        onFilter: (value, record) => record.type?.charging_type.includes(value),
      },
      {
        title: '자동충전 기준 금액/일',
        key: 'standard',
        dataIndex: 'jm_pay_setting',
        width: 300,
        align: 'center',
        render: (value) =>
          stringToMoneyFormat(value[TYPE[value?.charging_type]?.subKey]),
        sorter: (a, b) =>
          TYPE[a?.charging_type]?.subKey - TYPE[b?.charging_type]?.subKey,
      },
      {
        title: '유저메모',
        dataIndex: 'memo',
        align: 'center',
        render: (value) => strCut(value, 20),
      },
    ];
  }, [TYPE]);

  const childrenColumns = React.useMemo(() => {
    return [
      {
        title: '결제번호',
        key: 'jm_pay_id',
        dataIndex: 'jm_pay_id',
        width: 100,
        sorter: (a, b) => a.jm_pay_id - b.jm_pay_id,
      },
      {
        title: '결제일(시간)',
        dataIndex: 'created_at',
        width: 210,
        align: 'center',
        render: (value) => dayjs(value * 1000).format('YYYY-MM-DD (HH:mm:ss)'),
        sorter: (a, b) => a.created_at - b.created_at,
      },
      {
        title: '결제수단',
        dataIndex: 'order_route',
        width: 130,
        align: 'center',
        filters: Object.entries(ORDER_ROUTE).map(([key, v]) => ({
          text: v.label,
          value: key,
        })),
        onFilter: (value, record) => record.order_route.includes(value),
      },
      {
        title: '처리금액',
        dataIndex: 'price',
        width: 150,
        align: 'center',
        render: (value) => stringToMoneyFormat(value),
        sorter: (a, b) => a.price - b.price,
      },
      {
        title: '분류',
        dataIndex: 'type',
        width: 130,
        align: 'center',
        render: (value) => (
          <Tag color={JM_PAY_TYPE[value]?.color}>
            {JM_PAY_TYPE[value]?.label}
          </Tag>
        ),
      },
      {
        title: '충전 후 잔액',
        dataIndex: 'current_balance',
        width: 150,
        align: 'center',
        render: (value) => stringToMoneyFormat(value ?? 0),
      },
    ];
  }, []);

  const tableData = React.useMemo(() => {
    const newData = users.map((user) => ({
      key: user.user_id,
      ...user,
      histories: history
        .filter((h) => h.user_id === user.user_id)
        .sort((a, b) => b.created_at - a.created_at),
    }));

    return newData;
  }, [users, history]);
  /**!SECTION - Hooks */

  /**
   * SECTION - Render
   */
  return (
    <React.Fragment>
      <Content maxWidth={'100%'} padding={0} backgroundColor="none">
        <Title
          size={5}
          style={{ marginBottom: 7.5 }}
          subButton={<Button>엑셀로 다운로드</Button>}
        >
          이용자 목록({users.length})
        </Title>

        <Table
          className="boxShadow"
          bordered
          size="small"
          columns={columns}
          dataSource={tableData}
          scroll={{
            x: 'max-content',
          }}
          onRow={(record) => {
            return {
              onClick: () => setCurrentRow(record), // 행 클릭 시 이벤트 처리
            };
          }}
          expandable={{
            expandedRowRender: (currentRow) => (
              <Table
                size="small"
                columns={childrenColumns}
                dataSource={currentRow.histories}
                pagination={false}
                bordered
              />
            ),
          }}
        />
      </Content>

      {/* ===== 상세모달: start ===== */}
      <ModalLayout
        type="drawer"
        maxWidth={900}
        isOpen={currentRow !== null}
        open={currentRow !== null}
        title="상세보기"
        closable={true}
        onCancel={() => setCurrentRow(null)}
      >
        {currentRow && (
          <UserDetail
            defaultUserId={currentRow?.user_id ?? null}
            manageType="JM_PAY"
          />
        )}
      </ModalLayout>
      {/* ===== 상세모달: end ===== */}
    </React.Fragment>
  );
  /**!SECTION - Render */
};

export default AutoChargeUsers;
